import { createContext, useContext, useEffect, useRef, useState } from 'react';

import type {
  Deck,
  ContextType,
  CardData,
  SavedCardData,
  StatusBannerType,
  ActionMap,
  Config,
  FatalErrorState,
} from './types';
import { useTotalDecks } from './use-resize';

const SearchAndShuffleContext = createContext<ContextType | undefined>(
  undefined
);

const DeckCardContext = createContext<{
  data: CardData | undefined;
  deckIndex: number | undefined;
}>({ data: undefined, deckIndex: undefined });

export const useSearchAndShuffleContext = (): ContextType => {
  const context = useContext(SearchAndShuffleContext);
  if (!context) {
    throw new Error(
      'useSearchAndShuffleContext must be used within a SearchAndShuffleContext.Provider'
    );
  }
  return context;
};

export const SearchAndShuffleContextProvider: React.FC<{
  children: React.ReactNode;
  config: Config;
}> = ({ children, config }) => {
  const totalDecks = useTotalDecks();
  const [decks, setDecks] = useState<Deck[]>([]);
  const availableCards = useRef<CardData[]>([]);
  const [savedCards, setSavedCards] = useState<SavedCardData[]>([]);
  const [deletingSavedCards, setDeletingSavedCards] = useState<string[]>([]);
  const [actionMap, setActionMap] = useState<ActionMap>({});
  const cardLinksEnabled = useRef(true);
  const [isModalEnabled, setIsModalEnabled] = useState(false);
  const [consentTaken, setConsentTaken] = useState(false);
  const [tooltipOnCardId, setTooltipOnCardId] = useState<string | undefined>();
  const [executingAction, setExecutingAction] = useState(false);
  const [isShuffling, setIsShuffling] = useState<number | undefined>(undefined);
  const [filter, setFilter] = useState<string | undefined>(
    config?.filter?.options?.[0]?.value || undefined
  );
  const [fatalErrorState, setFatalErrorState] = useState<
    FatalErrorState | undefined
  >();
  const [statusBanner, setStatusBanner] = useState<StatusBannerType>({
    show: false,
    type: 'success',
    key: '',
  });
  const [loadingStatus, setLoadingStatus] = useState<'loading' | 'loaded'>(
    'loading'
  );

  useEffect(() => {
    setDecks((prevDecks) => {
      if (prevDecks.length >= totalDecks) {
        return prevDecks.slice(0, totalDecks);
      }
      return [
        ...prevDecks,
        ...Array.from({ length: totalDecks - prevDecks.length }, () => ({
          cards: [],
          loadedCount: 0,
        })),
      ];
    });
  }, [totalDecks]);

  return (
    <SearchAndShuffleContext.Provider
      value={{
        availableCards,
        decks,
        setDecks,
        savedCards,
        setSavedCards,
        deletingSavedCards,
        setDeletingSavedCards,
        cardLinksEnabled,
        isModalEnabled,
        setIsModalEnabled,
        statusBanner,
        setStatusBanner,
        consentTaken,
        setConsentTaken,
        tooltipOnCardId,
        setTooltipOnCardId,
        actionMap,
        setActionMap,
        isShuffling,
        setIsShuffling,
        executingAction,
        setExecutingAction,
        filter,
        setFilter,
        fatalErrorState,
        setFatalErrorState,
        config,
        loadingStatus,
        setLoadingStatus,
      }}
    >
      {children}
    </SearchAndShuffleContext.Provider>
  );
};

export const useDeckCard = (): CardData => {
  const deckCardContext = useContext(DeckCardContext);
  if (!deckCardContext.data) {
    throw new Error(
      'useDeckCard must be used within a DeckCardContext.Provider'
    );
  }
  return deckCardContext.data;
};

export const useDeckCardTyped = <T extends CardData['type']>(
  types: T[]
): Extract<CardData, { type: T }> => {
  const value = useDeckCard();

  if (types.includes(value.type as T)) {
    // TypeScript will narrow the type here
    return value as Extract<CardData, { type: T }>;
  }
  throw new Error(
    'useDeckCardTyped must be used within a DeckCardContext.Provider and with a matching type'
  );
};

export const useDeckCardIndex = (): number | undefined => {
  const deckCardContext = useContext(DeckCardContext);
  if (!deckCardContext) {
    throw new Error(
      'useDeckCardIndex must be used within a DeckCardContext.Provider'
    );
  }
  return deckCardContext.deckIndex;
};

export const DeckCardContextProvider: React.FC<{
  children: React.ReactNode;
  card: CardData;
  deckIndex: number;
}> = ({ children, card, deckIndex }) => {
  return (
    <DeckCardContext.Provider value={{ data: card, deckIndex }}>
      {children}
    </DeckCardContext.Provider>
  );
};
